import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { isDesktop } from 'react-device-detect';

import type { ApiQueryPromise } from '@/types';
import type { TicketModel } from '@/hooks/query/use-betting-info';
import type { SmpTicketResponse } from '@nf/types/ticket';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { useBetslipStore } from '@/store/betslip';
import { getFormData } from '@nf/utils-common/form';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { getResponseJwtHeader } from '@/utils/web';

export const betWithSmpTicket = async (
	ticketData: Record<string, any>
): Promise<ApiQueryPromise<SmpTicketResponse>> => {
	const authToken = getAuthToken();
	const { apiDomain } = useSiteStore.getState();
	const apiUrl = cloudUrlParser(`${apiDomain.ctcdDomain}/BetV2/ProcessBetSMP`);
	const formData = getFormData(ticketData);
	const response = await fetchComposer.postWithBearerBase(
		apiUrl, 
		{ body: formData }
	)(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useSmpBet = () => {
	const { updateAuthToken } = useAuthActions();
	const queryClient = useQueryClient();
	const { betAction, setIsConfirmBet, syncSmpTicket } = useBetslipStore.getState();
	
	return useMutation({
		mutationFn: (ticket: TicketModel) => {
			const ticketData = {
				AcceptAnyOdds: false,
				BettingId: uuidv4(),
				BetAction: betAction,
				Guid: ticket.Guid,
				isQuickBet: false,
				isTablet: false,
				MarketId: ticket.OddsID,
				Matchid: ticket.Matchid,
				Minbet: ticket.minBet,
				MobileVersion: 0,
				Odds: ticket.DisplayOdds,
				rewardSystemVoucherType: 0,
				SelectionCounts: ticket.specialChoices?.length ?? 0,
				Stake: ticket.stake,
			};
			return betWithSmpTicket(ticketData);
		},
		onSuccess: (response) => {
			if (response.data.ErrorCode === 0) {
				const itemList = response.data.Data?.ItemList ?? [];
				const smpBetTicket = itemList[0];
				const isAccepted = [0, 1].includes(smpBetTicket.ErrorCode);

				if (isAccepted) {
					syncSmpTicket(smpBetTicket);
					setIsConfirmBet(true);
				}

				// refetch my bets
				if (isDesktop) {
					queryClient.invalidateQueries({
						queryKey: [queryKeys.UNSETTLED_CATEGORY_INFO]
					});
					queryClient.invalidateQueries({
						queryKey: [queryKeys.UNSETTLED_STATUS_COUNT_INFO]
					});
				}
			}
		},
		onError: (error) => {
			console.error(error);
		},
		onSettled: data => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		},
	});
};